import { useState, type FC } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  IconButton,
  Popover,
  Box,
  Typography,
  Badge,
  Button,
  Menu,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DivVerticalDividerSmall } from '../common/StyledComponents';
import ExportButton from './ExportButton';
import TemplateDownloadButton from './TemplateDownloadButton';
import ImportButton from './ImportButton';
import type { VendorExistsFilter } from './MATGrid.types';

type MATActionsBarProps = {
  includeMatData: boolean;
  includeDiscovered: boolean;
  includeReviewedOnly: boolean;
  vendorExistsFilter: VendorExistsFilter;
  onIncludeMatDataChange: (value: boolean) => void;
  onIncludeDiscoveredChange: (value: boolean) => void;
  onIncludeReviewedOnlyChange: (value: boolean) => void;
  onVendorExistsFilterChange: (value: VendorExistsFilter) => void;
  onRefresh?: (() => void) | null;
};

const MATActionsBar: FC<MATActionsBarProps> = ({
  includeMatData,
  includeDiscovered,
  includeReviewedOnly,
  vendorExistsFilter,
  onIncludeMatDataChange,
  onIncludeDiscoveredChange,
  onIncludeReviewedOnlyChange,
  onVendorExistsFilterChange,
  onRefresh,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [vendorMenuAnchorEl, setVendorMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleVendorMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setVendorMenuAnchorEl(event.currentTarget);
  };

  const handleVendorMenuClose = () => {
    setVendorMenuAnchorEl(null);
  };

  const handleVendorExistsChange = (value: VendorExistsFilter) => {
    onVendorExistsFilterChange(value);
    handleVendorMenuClose();
  };

  const getVendorFilterLabel = (filter: VendorExistsFilter) => {
    switch (filter) {
      case 'true':
        return 'Has Vendor';
      case 'false':
        return 'No Vendor';
      default:
        return 'All';
    }
  };

  const popoverOpen = Boolean(filterAnchorEl);
  const vendorMenuOpen = Boolean(vendorMenuAnchorEl);

  // Count active discovery filters
  const activeFilterCount = (includeReviewedOnly ? 1 : 0) + (vendorExistsFilter !== 'all' ? 1 : 0);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={includeDiscovered}
            onChange={(e) => onIncludeDiscoveredChange(e.target.checked)}
            size="small"
            data-cy="checkbox-mat-discovered"
          />
        }
        label="Discovered"
        sx={{ marginRight: 0 }}
      />
      <Badge
        badgeContent={includeDiscovered ? activeFilterCount : 0}
        color="primary"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.65rem',
            height: 16,
            minWidth: 16,
          },
        }}
      >
        <IconButton
          size="small"
          onClick={handleFilterClick}
          disabled={!includeDiscovered}
          data-cy="btn-discovery-filters"
          sx={{
            color: includeDiscovered && activeFilterCount > 0 ? 'primary.main' : 'action.active',
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Badge>
      <Popover
        open={popoverOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Discovered Filters
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeReviewedOnly}
                onChange={(e) => onIncludeReviewedOnlyChange(e.target.checked)}
                size="small"
                data-cy="checkbox-mat-reviewed-only"
              />
            }
            label="Reviewed Only"
            sx={{ display: 'flex', mb: 1.5 }}
          />

          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Vendor Status
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleVendorMenuClick}
            endIcon={<KeyboardArrowDownIcon />}
            data-cy="btn-vendor-exists-filter"
            sx={{ minWidth: 120, justifyContent: 'space-between' }}
          >
            {getVendorFilterLabel(vendorExistsFilter)}
          </Button>
          <Menu
            anchorEl={vendorMenuAnchorEl}
            open={vendorMenuOpen}
            onClose={handleVendorMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem
              onClick={() => handleVendorExistsChange('all')}
              selected={vendorExistsFilter === 'all'}
              data-cy="menu-item-vendor-all"
            >
              All
            </MenuItem>
            <MenuItem
              onClick={() => handleVendorExistsChange('true')}
              selected={vendorExistsFilter === 'true'}
              data-cy="menu-item-vendor-has"
            >
              Has Vendor
            </MenuItem>
            <MenuItem
              onClick={() => handleVendorExistsChange('false')}
              selected={vendorExistsFilter === 'false'}
              data-cy="menu-item-vendor-no"
            >
              No Vendor
            </MenuItem>
          </Menu>
        </Box>
      </Popover>
      <DivVerticalDividerSmall />
      <FormControlLabel
        control={
          <Checkbox
            checked={includeMatData}
            onChange={(e) => onIncludeMatDataChange(e.target.checked)}
            size="small"
            data-cy="checkbox-mat-data"
          />
        }
        label="MAT Data"
        sx={{ marginRight: 1 }}
      />
      <ExportButton />
      <TemplateDownloadButton />
      <ImportButton onImported={onRefresh || undefined} />
    </div>
  );
};

export default MATActionsBar;
